import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Users, Heart, Share2, Zap, ShieldCheck, Target, TrendingUp, Search, Star, MessageCircle, Gift, CheckCircle2, Smartphone } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Como Vender IPTV para Amigos, Familiares e Indicações", href: "/blog/como-vender-iptv-amigos-familiares" }
  ];

  const sections = [
    {
      title: "O Círculo Quente: Suas Primeiras Vendas de Elite",
      content: (
        <div className="prose prose-invert max-w-none space-y-6 text-lg text-muted-foreground">
          <p>
            Muitos revendedores iniciantes cometem o erro de querer "conquistar o mundo" pelo Google Ads antes de olhar para o próprio lado. Em 2026, as <strong>vendas para amigos e familiares</strong> (o chamado Círculo Quente) continuam sendo o caminho mais rápido e seguro para validar seu negócio de IPTV e gerar o fluxo de caixa inicial necessário para escalar com o <strong>painel P2 PRO</strong>.
          </p>
          <p>
            Vender para conhecidos não é "pedir favor", é oferecer uma <strong>solução de economia e lazer</strong>. Quando você mostra que eles podem economizar R$ 300,00 por mês na conta de TV e ainda ter uma interface luxuosa como a do app One X, a venda acontece naturalmente por gratidão e confiança.
          </p>
          <div className="bg-electric/10 border-l-4 border-electric p-8 my-10 rounded-r-3xl relative overflow-hidden">
            <h4 className="text-white font-black mb-2 uppercase italic text-xl">Dica de Abordagem</h4>
            <p className="italic text-white/90">
              "Não chegue vendendo. Chegue perguntando quanto eles pagam na TV a cabo e ofereça uma alternativa melhor e mais barata para eles testarem sem compromisso."
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Como Abordar Amigos e Família sem ser Inconveniente",
      type: "grid" as const,
      content: (
        <>
          {[
            { t: "O Teste Descompromissado", d: "Ofereça o sinal P2 PRO durante um jogo importante ou lançamento de filme que eles queiram ver.", i: <Zap /> },
            { t: "Foco na Economia Real", d: "Mostre o quanto eles vão poupar no ano trocando a assinatura tradicional pela sua revenda Master.", i: <TrendingUp /> },
            { t: "Demonstração ao Vivo", d: "Abra o app One X no seu celular ou na TV deles e deixe que a fluidez do sistema venda por você.", i: <Smartphone /> },
            { t: "Gatilho da Exclusividade", d: "Diga que você está abrindo algumas vagas para um 'grupo de testes vip' e lembrou deles.", i: <Star /> },
            { t: "Suporte Personalizado", d: "Reforce que, por serem conhecidos, você mesmo dará todo o auxílio na configuração inicial.", i: <Heart /> },
            { t: "Peça Indicações", d: "Mesmo que não comprem, pergunte: 'Quem você conhece que também gostaria de economizar na TV?'.", i: <Share2 /> }
          ].map((item, i) => (
            <div key={i} className="p-8 rounded-[2.5rem] bg-white/[0.02] border border-white/5 hover:border-electric/30 transition-all group">
              <div className="w-14 h-14 rounded-2xl bg-electric/10 text-electric flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                {item.i}
              </div>
              <h3 className="text-2xl font-black tracking-tighter mb-4 italic uppercase">{item.t}</h3>
              <p className="text-muted-foreground leading-relaxed">{item.d}</p>
            </div>
          ))}
        </>
      )
    },
    {
      title: "Transformando Amigos em Promotores",
      content: (
        <div className="prose prose-invert max-w-none space-y-6">
          <p className="text-lg text-muted-foreground">
            A maior vantagem de vender para o círculo social é o <strong>poder do boca a boca</strong>. Um familiar satisfeito indicará você no churrasco de domingo, no grupo do trabalho e na academia. Como você oferece a estabilidade imbatível da <strong>P2 PRO</strong>, você não corre o risco de passar vergonha com sinal caindo – o que é fundamental quando sua reputação pessoal está em jogo.
          </p>
          <div className="p-10 rounded-[3rem] bg-white/[0.02] border border-white/5 my-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-electric/5 blur-[80px] rounded-full" />
            <h4 className="text-2xl font-black mb-6 italic uppercase tracking-tighter flex items-center gap-3">
              <Gift className="text-electric w-8 h-8" /> Checklist da Indicação Premiada
            </h4>
            <ul className="space-y-4">
              <li className="flex gap-4 items-start">
                <CheckCircle2 className="text-electric shrink-0 mt-1" />
                <p><span className="text-white font-bold">Bônus de Indicação:</span> 'Para cada amigo que assinar, você ganha 1 mês grátis'. Isso cria um exército de vendas.</p>
              </li>
              <li className="flex gap-4 items-start">
                <MessageCircle className="text-electric shrink-0 mt-1" />
                <p><span className="text-white font-bold">Feedback Constante:</span> Pergunte na primeira semana se está tudo certo. O cuidado fideliza mais que o preço.</p>
              </li>
              <li className="flex gap-4 items-start">
                <ShieldCheck className="text-electric shrink-0 mt-1" />
                <p><span className="text-white font-bold">Garantia P2 PRO:</span> Reforce que eles estão usando a tecnologia P2P, a mais moderna e estável do mercado.</p>
              </li>
            </ul>
          </div>
          <p className="text-lg text-muted-foreground">
            Vender para amigos e familiares em 2026 é o <strong>alicerce do seu império</strong>. Ao tratar cada venda com o profissionalismo de um Master de elite da <strong>P2 PRO</strong>, você garante não apenas as primeiras assinaturas, mas uma rede de contatos que sustentará seu crescimento orgânico por anos.
          </p>
        </div>
      )
    }
  ];

  return (
    <SEOPage
      category="Dicas Como Revender Iptv"
      title="Como Vender IPTV para Amigos e Familiares: Guia de Confiança 2026"
      subtitle="Comece sua revenda com o pé direito. Aprenda a abordar seu círculo social de forma profissional, gerando vendas e indicações valiosas."
      heroImage="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&q=80&w=1200"
      introParagraphs={[
        "Muitas pessoas têm receio de oferecer seu serviço para conhecidos, mas a verdade é que você está fazendo um favor a eles ao apresentar uma forma de ter entretenimento de cinema por um preço justo. Em 2026, a indicação pessoal ainda é a moeda de maior valor no mercado de streaming.",
        "Neste artigo, vamos te mostrar como quebrar o gelo e abordar amigos, familiares e colegas de trabalho de forma profissional, sem parecer 'o chato das vendas'. Vamos focar em como demonstrar o valor real do sistema P2 PRO e como transformar esses primeiros clientes em promotores fervorosos da sua marca.",
        "Com a tecnologia de elite da P2 PRO, você tem a segurança de vender um produto que realmente funciona, protegendo sua credibilidade pessoal e garantindo que seus amigos fiquem satisfeitos e gratos pela indicação."
      ]}
      benefits={[
        { title: "Confiança Imediata", description: "Seus amigos já confiam em você, o que elimina 90% das objeções comuns de segurança." },
        { title: "Custo Zero de Aquisição", description: "Você não gasta com anúncios para vender para quem já está no seu WhatsApp." },
        { title: "Feedback Sincero", description: "Conhecidos tendem a dar feedbacks mais diretos, ajudando você a ajustar seu atendimento inicial." },
        { title: "Efeito Bola de Neve", description: "Uma venda bem feita para um familiar pode gerar dezenas de indicações em poucos dias." }
      ]}
      howItWorks={[
        { title: "Mapeamento de Contatos", description: "Liste as 20 pessoas mais próximas que consomem filmes, séries ou futebol." },
        { title: "Abordagem Casual", description: "Inicie uma conversa normal e insira o assunto do entretenimento de forma natural." },
        { title: "Demonstração Visual", description: "Mostre o app One X rodando na sua TV ou celular. O visual vende sozinho." },
        { title: "Fechamento e Pós-Venda", description: "Ofereça um bônus de inauguração e garanta que a instalação seja perfeita." }
      ]}
      diferenciais={[
        { title: "Sinal P2P Anti-Trava", description: "A garantia de que você não terá problemas técnicos com pessoas do seu convívio social." },
        { title: "Apps de Luxo", description: "O design premium que deixa qualquer um impressionado logo no primeiro uso." },
        { title: "Suporte Master", description: "Conte com nossa equipe para garantir que suas primeiras vendas sejam um sucesso absoluto." }
      ]}
      faq={[
        { question: "Devo dar desconto para familiares?", answer: "Não recomendamos. Valorize seu trabalho. O melhor 'desconto' é oferecer um suporte de elite e um sinal que nunca cai." },
        { question: "Como agir se eles acharem caro?", answer: "Compare com o preço da TV a cabo tradicional e do cinema. Mostre que em 2 meses eles já economizaram o valor da assinatura." },
        { question: "E se o sinal cair na casa deles?", answer: "Como parceiro P2 PRO, isso é raríssimo. Mas se ocorrer, você tem suporte prioritário para resolver e não se queimar." },
        { question: "Como pedir indicações sem ser chato?", answer: "Espere o cliente elogiar o sinal e diga: 'Que bom que gostou! Se tiver algum amigo querendo economizar também, me indica que te dou um bônus'." },
        { question: "Devo fazer grupo com a família?", answer: "Pode ser uma boa ideia para postar a grade de jogos do final de semana e gerar desejo coletivo." },
        { question: "Qual o melhor momento para oferecer?", answer: "Antes de grandes eventos: final de campeonato, estreia de série famosa ou feriados prolongados." }
      ]}
      breadcrumbs={breadcrumbs}
      sections={sections}
      ctaTitle="💎 Comece seu Império com quem você Confia"
      ctaDescription="Leve o melhor do entretenimento para seus amigos com a tecnologia P2 PRO. Seja um Master agora."
      ctaButtonText="Ser um Revendedor Master"
    />
  );
};

export const Route = createFileRoute("/blog/como-vender-iptv-amigos-familiares")({
  head: () => ({
    meta: [
      { title: "Como Vender IPTV para Amigos e Familiares: Guia 2026 | P2 PRO" },
      { name: "description", content: "Aprenda as melhores técnicas para vender IPTV para seu círculo social. Transforme amigos e familiares em clientes fiéis com a qualidade P2 PRO." },
      { name: "keywords", content: "vender iptv amigos, marketing boca a boca streaming, vender iptv família, prospecção orgânica iptv" }
    ],
  }),
  component: ArticlePage,
});